import React from 'react';

import DatePicker from '@hero/ui-kit/components/DatePicker';
import CalendarIcon from '@hero/ui-kit/icons/branding/CalendarIcon';
import CallIcon from '@hero/ui-kit/icons/branding/CallIcon';
import CheckIcon from '@hero/ui-kit/icons/utility/CheckIcon';
import Button from '@hero/ui-kit/inputs/Button';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import Section from '@hero/ui-kit/layout/Section';
import SideBar from '../../../../../components/SideBar';
import MenuDotsIcon from '../../../../../icons/MenuDotsIcon';
import { getFormattedDate, getIsBeforeComparedDate, parseToDate } from '../../../../../utils/date';
import useMemberCheckin from '../../../api/useMemberCheckin';
import useMemberSendMonthlyCallReminder from '../../../api/useMemberSendMonthlyCallReminder';
import { OrganizationMemberDetails } from '../../../types';
import MonthlyCallReminderModal from '../../MonthlyCallReminderModal';
import * as Style from './style.module.scss';

const timezoneAbbr =
    new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')
        ?.value?.replace(/^([ECMP])[SD]T$/, '$1T') ?? '';

const MonthlyCheckIn: React.FC<{ data: OrganizationMemberDetails }> = ({ data }) => {
    const rtmCheckin = data?.member_info.rtm_checkin;

    const [showMonthlyCheckInSidebar, setShowMonthlyCheckInSidebar] = React.useState(false);
    const [showMonthlyCallReminderModal, setShowMonthlyCallReminderModal] = React.useState(false);
    const memberCheckinMutation = useMemberCheckin();
    const monthlyCallReminder = useMemberSendMonthlyCallReminder();
    const [toggled, setToggled] = React.useState({
        completed: false,
        schedule_date: false,
        call_reminder: false
    });
    const [selectedDate, setSelectedDate] = React.useState<Date | null>(
        rtmCheckin?.next_checkin_time ? parseToDate(rtmCheckin.next_checkin_time) : null
    );
    const isCaregiver = !data?.member_info.member_id;

    // Start and min date on calendar should be 'today', unless check-in is complete, then it should be first day of next month
    // Completed month should be locked
    const now = new Date();
    const startDate =
        rtmCheckin?.status === 'complete'
            ? new Date(now.getFullYear(), now.getMonth() + 1, 1)
            : now;

    const memberCheckinComplete = () => {
        const id = data.member_info.hero_user_id || data.member_info.member_id;
        if (!id) return;

        memberCheckinMutation.mutate({
            payload: {
                hero_user_id: id,
                action: 'complete'
            }
        });

        setToggled({
            ...toggled,
            completed: false
        });
    };

    const memberSchedule = () => {
        const id = data.member_info.hero_user_id || data.member_info.member_id;
        if (!id || !selectedDate) return;

        const utcDate = new Date(selectedDate.getTime() + selectedDate.getTimezoneOffset() * 60000);

        memberCheckinMutation.mutate({
            payload: {
                hero_user_id: id,
                action: 'schedule_date',
                scheduled_date: getFormattedDate(utcDate, "yyyy-MM-dd'T'HH:mm:ss'Z'")
            }
        });

        setTimeout(() => {
            setToggled({
                ...toggled,
                schedule_date: false
            });
        }, 500);
    };

    const sendCallReminder = () => {
        const id = data.member_info.member_id;
        if (!id) return;

        monthlyCallReminder.mutate(
            { member_id: id },
            {
                onSuccess: () => {
                    setToggled({
                        ...toggled,
                        call_reminder: false
                    });
                }
            }
        );
    };

    return (
        <>
            {data.member_info.rtm_checkin ? (
                <div>
                    <Button
                        variant="outlineSecondary"
                        onClick={() => setShowMonthlyCheckInSidebar(true)}
                        className={`${Style.mainButton} ${
                            rtmCheckin?.status === 'complete'
                                ? Style.complete
                                : rtmCheckin?.status === 'not_done'
                                  ? Style.notDone
                                  : ''
                        }`}
                    >
                        <span>
                            Monthly Check-In:{' '}
                            {data.member_info.rtm_checkin.next_checkin_time &&
                                getFormattedDate(
                                    parseToDate(data.member_info.rtm_checkin.next_checkin_time),
                                    'MMM d, yyyy'
                                )}
                        </span>
                        <MenuDotsIcon size="small" styles={{ marginRight: '-1rem' }} />
                    </Button>
                </div>
            ) : (
                <div>
                    <Button
                        disabled={!data.member_info.can_send_disengaged_reminder}
                        variant="outlineSecondary"
                        onClick={() => setShowMonthlyCallReminderModal(true)}
                    >
                        Send call reminder
                    </Button>

                    {!data.member_info.can_send_disengaged_reminder &&
                        data.member_info.disengaged_reminders?.length > 0 && (
                            <small>
                                Sent on{' '}
                                {getFormattedDate(
                                    parseToDate(
                                        data.member_info.disengaged_reminders[0].reminder_sent
                                    ),
                                    'MMM d, yyyy HH:mm:ss'
                                )}
                            </small>
                        )}
                </div>
            )}

            <MonthlyCallReminderModal
                memberId={data?.member_info.member_id || 0}
                heroUserId={data?.member_info.hero_user_id || 0}
                externalControls={[showMonthlyCallReminderModal, setShowMonthlyCallReminderModal]}
                memberName={data?.member_info.first_name || ''}
                isCaregiver={isCaregiver}
            />

            {showMonthlyCheckInSidebar && (
                <SideBar
                    listenToKeydown
                    onOutsideClick={() => {
                        setShowMonthlyCheckInSidebar(false);
                    }}
                    className={Style.sidebar}
                >
                    <Section paddingHorizontal="small" paddingVertical="regular">
                        <h5>Monthly Check-In</h5>
                        <strong className="p-large">
                            {rtmCheckin?.next_checkin_time &&
                                getFormattedDate(
                                    parseToDate(rtmCheckin.next_checkin_time),
                                    'MMM d, yyyy @ hh:mm a'
                                )}{' '}
                            {timezoneAbbr && `(${timezoneAbbr})`}{' '}
                            {rtmCheckin?.status === 'not_done' && (
                                <span className="color-error">OVERDUE</span>
                            )}
                        </strong>

                        {/* MARK AS COMPLETED */}
                        <Section noDefaultPadding marginVertical="regular">
                            <ClickableOpacity
                                onClick={() => {
                                    setToggled({
                                        ...toggled,
                                        completed: !toggled.completed
                                    });
                                }}
                                alt="Mark as Complete"
                                disabled={
                                    memberCheckinMutation.isPending ||
                                    rtmCheckin?.status === 'complete'
                                }
                                className={rtmCheckin?.status === 'complete' ? 'ui-disabled' : ''}
                            >
                                <Section
                                    noDefaultPadding
                                    paddingVertical="small"
                                    className="icon-title"
                                >
                                    <CheckIcon size="utility" type="utility" />
                                    <strong className="p-large">
                                        {rtmCheckin?.status === 'complete'
                                            ? 'Completed'
                                            : 'Mark as Complete'}
                                    </strong>
                                </Section>
                            </ClickableOpacity>

                            {toggled.completed && (
                                <Section
                                    noDefaultPadding
                                    marginBottom="regular"
                                    className={Style.buttonGroup}
                                >
                                    <Button
                                        variant="primary"
                                        onClick={memberCheckinComplete}
                                        disabled={
                                            memberCheckinMutation.isPending ||
                                            rtmCheckin?.status === 'complete'
                                        }
                                    >
                                        Confirm
                                    </Button>

                                    <Button
                                        variant="outlineSecondary"
                                        onClick={() => {
                                            setToggled({ ...toggled, completed: false });
                                        }}
                                        disabled={
                                            memberCheckinMutation.isPending ||
                                            rtmCheckin?.status === 'complete'
                                        }
                                    >
                                        Abort
                                    </Button>
                                </Section>
                            )}

                            <hr />

                            {/* SCHEDULE CALENDAR */}
                            <ClickableOpacity
                                onClick={() => {
                                    setToggled({
                                        ...toggled,
                                        schedule_date: !toggled.schedule_date
                                    });
                                }}
                                alt="Schedule Preferred Date"
                                fullWidth
                            >
                                <Section
                                    noDefaultPadding
                                    paddingVertical="small"
                                    className="icon-title"
                                >
                                    <CalendarIcon size="utility" type="utility" />
                                    <strong className="p-large">Schedule preferred date</strong>
                                </Section>
                            </ClickableOpacity>

                            {toggled.schedule_date && (
                                <Section noDefaultPadding marginBottom="regular">
                                    <div
                                        className={Style.datePicker}
                                        style={
                                            {
                                                '--tz-abbr': `"(${timezoneAbbr})"`
                                            } as React.CSSProperties
                                        }
                                    >
                                        <DatePicker
                                            onChange={setSelectedDate}
                                            selected={selectedDate}
                                            startDate={startDate}
                                            minDate={startDate}
                                            maxDate={
                                                new Date(
                                                    new Date().getFullYear(),
                                                    new Date().getMonth() + 2,
                                                    0
                                                )
                                            }
                                            inline
                                            showTimeSelect
                                            timeIntervals={15}
                                            minTime={new Date(new Date().setHours(8, 0, 0, 0))}
                                            maxTime={new Date(new Date().setHours(20, 0, 0, 0))}
                                            timeCaption={`Time (${timezoneAbbr})`}
                                        />
                                    </div>

                                    <Section
                                        noDefaultPadding
                                        marginTop="regular"
                                        className={Style.buttonGroup}
                                    >
                                        <Button
                                            variant="primary"
                                            onClick={memberSchedule}
                                            disabled={
                                                memberCheckinMutation.isPending ||
                                                getIsBeforeComparedDate(
                                                    selectedDate || new Date(),
                                                    startDate
                                                )
                                            }
                                        >
                                            Confirm
                                        </Button>

                                        <Button
                                            variant="outlineSecondary"
                                            disabled={memberCheckinMutation.isPending}
                                            onClick={() => {
                                                setSelectedDate(
                                                    rtmCheckin?.next_checkin_time
                                                        ? new Date(rtmCheckin.next_checkin_time)
                                                        : null
                                                );
                                                setToggled({ ...toggled, schedule_date: false });
                                            }}
                                        >
                                            Abort
                                        </Button>
                                    </Section>
                                </Section>
                            )}

                            <hr />

                            {/* CALL REMINDER */}
                            <ClickableOpacity
                                onClick={() =>
                                    setToggled({
                                        ...toggled,
                                        call_reminder: !toggled.call_reminder
                                    })
                                }
                                alt="Send Call Reminder"
                                disabled={data.member_info.disengaged_reminders?.length > 0}
                                className={`align-left ${
                                    data.member_info.disengaged_reminders?.length > 0
                                        ? 'ui-disabled'
                                        : ''
                                }`}
                            >
                                <Section
                                    noDefaultPadding
                                    paddingVertical="small"
                                    className="icon-title"
                                >
                                    <CallIcon size="utility" type="utility" />

                                    {data.member_info.disengaged_reminders?.length > 0 ? (
                                        <div style={{ lineHeight: '1.4' }}>
                                            <strong className="p-large">Call Reminder Sent</strong>
                                            <div>
                                                {
                                                    data.member_info.disengaged_reminders[0]
                                                        .reminder_sent
                                                }
                                            </div>
                                        </div>
                                    ) : (
                                        <strong className="p-large">Send Call Reminder</strong>
                                    )}
                                </Section>
                            </ClickableOpacity>

                            {toggled.call_reminder && (
                                <Section noDefaultPadding marginBottom="regular">
                                    <p>
                                        {data.member_info.first_name} will receive a reminder across
                                        their connected channels (SMS, Email, App, and Dispenser):
                                    </p>
                                    <div className={Style.preview}>
                                        <p>
                                            <strong>📣 Important Reminder</strong>
                                        </p>

                                        {isCaregiver ? (
                                            <p>
                                                To stay enrolled in your provider&apos;s remote care
                                                program, {data.member_info.first_name} must attend
                                                their monthly check-in call.
                                                <br />
                                                Missing this call puts their Hero subscription at
                                                risk.
                                                <br />
                                                Call provider NOW to avoid any interruptions in{' '}
                                                {data.member_info.first_name}'s care and access to
                                                the Hero platform.
                                            </p>
                                        ) : (
                                            <p>
                                                To stay enrolled in your provider&apos;s remote care
                                                program, you must attend your monthly check-in call.
                                                <br />
                                                Missing this call puts your Hero subscription at
                                                risk.
                                                <br />
                                                Call provider NOW to avoid any interruptions in your
                                                care and access to the Hero platform.
                                            </p>
                                        )}
                                    </div>

                                    <Section
                                        noDefaultPadding
                                        marginTop="regular"
                                        className={Style.buttonGroup}
                                    >
                                        <Button
                                            disabled={monthlyCallReminder.isPending}
                                            onClick={sendCallReminder}
                                        >
                                            Confirm
                                        </Button>

                                        <Button
                                            variant="outlineSecondary"
                                            disabled={monthlyCallReminder.isPending}
                                            onClick={() => {
                                                setToggled({
                                                    ...toggled,
                                                    call_reminder: false
                                                });
                                            }}
                                        >
                                            Abort
                                        </Button>
                                    </Section>
                                </Section>
                            )}
                        </Section>
                    </Section>
                </SideBar>
            )}
        </>
    );
};

export default MonthlyCheckIn;
